import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';

const TYPE_ICON: Record<string, string> = {
  ebook: 'ri-book-2-line',
  course: 'ri-play-circle-line',
  mentorship: 'ri-user-star-line',
  other: 'ri-shopping-bag-line',
};

export default function CartDrawer() {
  const { items, count, total, removeItem, clearCart, isOpen, closeCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleCheckout = () => {
    closeCart();
    navigate('/carrinho');
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[80] bg-black/50"
          style={{ backdropFilter: 'blur(4px)' }}
          onClick={closeCart}
        />
      )}

      {/* Drawer */}
      <div
        className="fixed top-0 right-0 h-full z-[90] flex flex-col w-full max-w-sm transition-transform duration-400"
        style={{
          background: 'linear-gradient(160deg, rgba(14,14,24,0.99) 0%, rgba(10,10,16,0.97) 100%)',
          borderLeft: '1px solid rgba(212,175,55,0.18)',
          boxShadow: '-8px 0 64px rgba(0,0,0,0.7)',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transitionTimingFunction: 'cubic-bezier(0.32, 0.72, 0, 1)',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-5 shrink-0"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}
        >
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 flex items-center justify-center bg-[#D4AF37]/15 rounded-lg">
              <i className="ri-shopping-cart-2-line text-[#D4AF37] text-base"></i>
            </span>
            <div>
              <h2 className="text-white font-bold text-sm">Meu Carrinho</h2>
              <p className="text-[10px] text-gray-500">{count} {count === 1 ? 'item' : 'itens'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {count > 0 && (
              <button
                onClick={clearCart}
                className="text-xs text-gray-600 hover:text-red-400 transition-colors cursor-pointer whitespace-nowrap px-2 py-1"
              >
                Limpar
              </button>
            )}
            <button
              onClick={closeCart}
              className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all cursor-pointer"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto">
          {count === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
              <span className="w-16 h-16 flex items-center justify-center bg-white/5 border border-white/10 rounded-2xl mb-4">
                <i className="ri-shopping-cart-2-line text-3xl text-gray-600"></i>
              </span>
              <p className="text-white font-semibold text-sm mb-2">Carrinho vazio</p>
              <p className="text-gray-500 text-xs mb-6">Explore nossos cursos, livros e mentorias</p>
              <div className="flex flex-col gap-2 w-full">
                <Link to="/cursos" onClick={closeCart} className="flex items-center justify-center gap-2 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm text-gray-300 hover:text-white transition-all cursor-pointer">
                  <i className="ri-play-circle-line text-sky-400"></i> Ver Cursos
                </Link>
                <Link to="/livros" onClick={closeCart} className="flex items-center justify-center gap-2 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm text-gray-300 hover:text-white transition-all cursor-pointer">
                  <i className="ri-book-2-line text-[#D4AF37]"></i> Ver Livros
                </Link>
              </div>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {items.map(item => (
                <div
                  key={item.cartId}
                  className="flex gap-3 p-3 rounded-xl bg-white/5 border border-white/[0.08] group hover:border-white/15 transition-all"
                >
                  <div className="w-14 h-18 rounded-lg overflow-hidden shrink-0 bg-white/5" style={{ height: '72px' }}>
                    <img src={item.cover} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-xs font-semibold text-white leading-tight line-clamp-2 flex-1">{item.name}</p>
                      <button
                        onClick={() => removeItem(item.cartId)}
                        className="w-5 h-5 flex items-center justify-center rounded-full bg-white/5 hover:bg-red-500/20 text-gray-600 hover:text-red-400 transition-all cursor-pointer shrink-0 mt-0.5"
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className={`w-3.5 h-3.5 flex items-center justify-center ${TYPE_ICON[item.type] ? 'text-[#D4AF37]' : 'text-gray-400'}`}>
                        <i className={`${TYPE_ICON[item.type] ?? 'ri-shopping-bag-line'} text-xs`}></i>
                      </span>
                      <span className="text-[10px] text-gray-500 capitalize">{item.variationName}</span>
                    </div>
                    <p className="text-sm font-bold text-[#D4AF37] mt-1.5">
                      R$ {item.price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {count > 0 && (
          <div
            className="p-5 shrink-0 space-y-3"
            style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}
          >
            {/* Savings badge */}
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-400">Subtotal ({count} {count === 1 ? 'item' : 'itens'})</span>
              <span className="text-white font-semibold">R$ {total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/20 rounded-xl">
              <i className="ri-qr-code-line text-green-400 text-sm shrink-0"></i>
              <p className="text-[10px] text-green-400">Pague com Pix e ganhe 5% de desconto extra!</p>
            </div>
            <button
              onClick={handleCheckout}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A0A0F] rounded-xl font-bold text-sm transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-secure-payment-line text-base"></i>
              Finalizar Compra · R$ {total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </button>
            <Link
              to="/cursos"
              onClick={closeCart}
              className="w-full flex items-center justify-center py-2.5 text-xs text-gray-500 hover:text-gray-300 transition-colors cursor-pointer"
            >
              Continuar comprando
            </Link>
          </div>
        )}
      </div>
    </>
  );
}
